// Direct asset access against Azure Blob Storage - the per-environment configuration (domain and
// container) and the SAS token the private container needs.
//
// Starting point: images, thumbnails and PDFs never travel through the backend API, they are
// fetched straight from the blob container (see provider.ts's `assetsBase`) - a binary fetch has no
// server-side logic to save, and the published DOKA OpenAPI contract defines no asset routes. While
// that container was public (or had a CDN/Front Door in front), the bare URL was enough.
//
// New: the container is private, and access goes through a user-delegation SAS token the API hands
// out:
//
//   GET {VITE_APP_API_URL}/azure/GetStorageContainerSasToken/{storage}/{container}
//   -> { "data": "skoid=...&se=2026-08-27T10%3A10%3A55Z&sr=c&sp=rl&sig=..." }
//
// The token is the plain query string and is valid for the whole container, so every asset URL
// becomes `https://{storage}/{container}/assets/{assetId}.jpg?{token}`. It is short-lived (~1h, see
// its `se=`), which is why it is kept centrally here, renewed before it expires, and appended only
// at render time: the URLs sitting in the react-query cache and in the wishlist (localStorage) stay
// token-free and therefore never go stale. On top of that its validity is re-checked on a timer
// (every 5 minutes by default, VITE_APP_ASSETS_SAS_CHECK_SECONDS) so a page left open renews the
// token on its own instead of quietly starting to 403.
//
// Configuration (.env / the pipeline's variables-<env>.yml, different per DEV/TEST/PROD):
//   VITE_APP_ASSETS_STORAGE   - storage account domain, e.g. azwestdevpih.blob.core.windows.net
//   VITE_APP_ASSETS_CONTAINER - container, e.g. catalog (default: catalog)
//   VITE_APP_ASSETS_SAS       - true/false forces token usage (empty = decide automatically)
//   VITE_APP_ASSETS_URL       - the previous single-variable root; still honoured, and split back
//                               into domain + container when the two above are not set.
//   VITE_APP_ASSETS_SAS_CHECK_SECONDS - how often the token's validity is checked (default 300)
//   VITE_APP_ASSETS_SAS_SKEW_SECONDS  - how long before its `se=` it is renewed (default 300)

import { useCallback, useSyncExternalStore } from 'react';

/** How this module reaches the (authenticated) API - injected, see `configureSasTokenSource`. */
export type SasTokenRequest = (url: string) => Promise<unknown>;

const env = (name: keyof ImportMetaEnv): string => (import.meta.env[name] ?? '').trim();

const stripTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

/** `https://host/` / `host/` -> `host`, so an accidentally pasted scheme does no harm. */
const toHost = (value: string): string => stripTrailingSlash(value.replace(/^https?:\/\//i, ''));

const DEFAULT_CONTAINER = 'catalog';

/**
 * Domain and container: either configured explicitly (the normal way - one variable per
 * environment) or recovered from an already configured `VITE_APP_ASSETS_URL`. The latter keeps
 * existing .env files working unchanged and still lets them fetch a token, since the endpoint URL
 * needs both parts separately.
 */
const resolveTarget = (): { storage: string; container: string; root: string } => {
    const explicitStorage = toHost(env('VITE_APP_ASSETS_STORAGE'));
    const explicitContainer = stripTrailingSlash(env('VITE_APP_ASSETS_CONTAINER')).replace(/^\/+/, '');
    const legacyRoot = stripTrailingSlash(env('VITE_APP_ASSETS_URL'));

    if (explicitStorage) {
        const container = explicitContainer || DEFAULT_CONTAINER;
        return { storage: explicitStorage, container, root: `https://${explicitStorage}/${container}` };
    }

    if (!legacyRoot) {
        return { storage: '', container: explicitContainer, root: '' };
    }

    // Only an absolute URL can be split - a relative root (e.g. a path behind the same reverse
    // proxy) simply ends up without storage/container, and therefore without a SAS token.
    try {
        const parsed = new URL(legacyRoot);
        const [firstSegment = ''] = parsed.pathname.split('/').filter(Boolean);
        return { storage: parsed.host, container: explicitContainer || firstSegment, root: legacyRoot };
    } catch {
        return { storage: '', container: explicitContainer, root: legacyRoot };
    }
};

const target = resolveTarget();

/**
 * The root that `assets/` and `assets/tn/` live under - empty when nothing is configured, in which
 * case provider.ts falls back to the static sample under public/catalog-data.
 */
export const assetsRoot = target.root;

/** Storage domain and container exactly as they appear in the endpoint URL (exported for diagnostics). */
export const assetsStorage = target.storage;
export const assetsContainer = target.container;

// Assets live on a DIFFERENT origin than the app (a blob storage domain), so the first thumbnail
// pays for a fresh DNS lookup, TCP handshake and TLS negotiation before a single byte moves. This
// starts that handshake while the catalog JSON is still downloading - by the time the first grid
// renders, the connection is usually already open. Costs one <link> tag and nothing else.
if (typeof document !== 'undefined' && target.storage) {
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = `https://${target.storage}`;
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
}

// Automatic: a real Azure blob endpoint is private and needs a token; local Azurite
// (127.0.0.1:10000) and a public CDN/Front Door domain do not. VITE_APP_ASSETS_SAS overrides that
// in both directions.
const sasFlag = env('VITE_APP_ASSETS_SAS').toLowerCase();
export const isSasRequired = sasFlag
    ? sasFlag === 'true'
    : Boolean(target.storage) && /\.blob\.core\.windows\.net$/i.test(target.storage);

/** The token endpoint this app calls, fully built - exported for diagnostics (see assetDebug.ts). */
export const sasEndpointUrl = `${env('VITE_APP_API_URL')}/azure/GetStorageContainerSasToken/${encodeURIComponent(
    target.storage
)}/${encodeURIComponent(target.container)}`;

/** A positive number of seconds from .env, in ms - anything missing or unusable falls back. */
const seconds = (name: keyof ImportMetaEnv, fallbackSeconds: number): number => {
    const configured = Number(env(name));
    return (Number.isFinite(configured) && configured > 0 ? configured : fallbackSeconds) * 1000;
};

// How often the token is checked for validity while the app is open (VITE_APP_ASSETS_SAS_CHECK_SECONDS,
// default 5 minutes). The check itself is local and free - it compares the token's own `se=` against
// the clock; only a token that is gone or about to go triggers an actual request. Polling rather
// than one timer armed at the expiry on purpose: a laptop coming back from sleep, a clock jump or a
// token cut short server-side would all silently outlive a single long timeout.
const CHECK_INTERVAL_MS = seconds('VITE_APP_ASSETS_SAS_CHECK_SECONDS', 5 * 60);
// How far ahead of `se=` the token counts as expired and is renewed
// (VITE_APP_ASSETS_SAS_SKEW_SECONDS, default 5 minutes) - an image that starts loading now must not
// have its token expire halfway through.
const REFRESH_SKEW_MS = seconds('VITE_APP_ASSETS_SAS_SKEW_SECONDS', 5 * 60);
// Without an `se=` (unexpected format) the token is refetched after this long, just to be safe.
const FALLBACK_LIFETIME_MS = 30 * 60 * 1000;
// After a failure, don't knock again for every single image.
const RETRY_DELAY_MS = 30 * 1000;

// ── Live trace ─────────────────────────────────────────────────────────────
// Answers "how does the app get its token, and how does it build the links?" while it is actually
// running - switched on with ?assettrace=1 (or pih.trace() in the console, see assetDebug.ts) and
// remembered for the tab session, the same shape as apiTest.ts's ?apitest= switch. Off, this costs
// one boolean check per URL.
const TRACE_STORAGE_KEY = 'pih.assettrace';

const readTraceFlag = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    const fromQuery = new URLSearchParams(window.location.search).get('assettrace');
    if (fromQuery !== null) {
        const on = fromQuery !== '0' && fromQuery.toLowerCase() !== 'false';
        window.sessionStorage.setItem(TRACE_STORAGE_KEY, on ? '1' : '');
        return on;
    }
    return window.sessionStorage.getItem(TRACE_STORAGE_KEY) === '1';
};

let tracing = readTraceFlag();

/** Turns the running commentary on/off for this tab (pih.trace(true/false)). */
export const setAssetTrace = (on: boolean): boolean => {
    tracing = on;
    if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(TRACE_STORAGE_KEY, on ? '1' : '');
    }
    return tracing;
};

export const isAssetTrace = (): boolean => tracing;

const trace = (...args: unknown[]): void => {
    if (tracing) {
        // eslint-disable-next-line no-console
        console.info('%c[assets]', 'color:#004588;font-weight:600', ...args);
    }
};

/** One asset URL as provider.ts assembled it - what `pih.lastUrls()` hands back. */
export interface AssetUrlRecord {
    assetId: string;
    mimeType?: string;
    /** 'assets' (original) or 'assets/tn' (thumbnail). */
    dir: string;
    /** Extension guessed from the mimeType - the usual suspect behind a 404. */
    extension: string;
    /** The URL before any token is appended. */
    url: string;
}

// Always filled, whether tracing or not (a push into a 50-element ring buffer is nothing next to
// rendering a grid): the question "which URL did the app actually build?" usually comes up AFTER
// the page has loaded, and re-loading it with a switch on would throw away the very case at hand.
const MAX_RECENT = 50;
const recent: AssetUrlRecord[] = [];

/**
 * Called by provider.ts for every asset URL it assembles - the one place that still knows assetId,
 * mimeType and directory, which is exactly what one needs when a URL 404s.
 */
export const recordAssetUrl = (record: AssetUrlRecord): void => {
    recent.push(record);
    if (recent.length > MAX_RECENT) {
        recent.shift();
    }
    trace(`built ${record.dir}/${record.assetId}${record.extension}`, record.url);
};

/** The last URLs the app built, newest last. */
export const recentAssetUrls = (count = 10): AssetUrlRecord[] => recent.slice(-count);

let request: SasTokenRequest | null = null;
let token: string | null = null;
let expiresAt = 0;
let retryAfter = 0;
let inFlight: Promise<string | null> | null = null;
let checkTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

/**
 * Registers how the token is fetched. Comes from CatalogDataProvider.tsx, because only there is the
 * authenticated request function reachable (@uag/react-core, bearer token from the OIDC context).
 * Deliberately NOT provider.ts's `CatalogJsonFetcher`: its `finishPayload` throws on a response that
 * unwraps to a string - and a SAS token is exactly that.
 */
export const configureSasTokenSource = (next: SasTokenRequest): void => {
    request = next;
};

/** The current token, or `null` while none has been fetched. */
export const currentSasToken = (): string | null => token;

/** When the current token expires (epoch ms, 0 = none fetched yet) - for diagnostics. */
export const sasTokenExpiresAt = (): number => expiresAt;

/** Expiry taken from the token's own `se=` parameter (epoch ms). */
const parseExpiry = (value: string): number => {
    const expiry = new URLSearchParams(value).get('se');
    const parsed = expiry ? Date.parse(expiry) : Number.NaN;
    return Number.isNaN(parsed) ? Date.now() + FALLBACK_LIFETIME_MS : parsed;
};

/**
 * Digs the token string out of the response. The contract is `{ "data": "<token>" }`, but depending
 * on the client the bare token or a whole AxiosResponse can arrive just as well - so up to three
 * wrappers are peeled off instead of committing to one shape (same stance as provider.ts's
 * `unwrapPayload`).
 */
const extractToken = (payload: unknown): string | null => {
    let current = payload;

    for (let step = 0; step < 3; step++) {
        if (typeof current === 'string') {
            // A leading '?' would end up doubled once the query string is appended.
            const trimmed = current.trim().replace(/^\?/, '');
            return trimmed.length > 0 ? trimmed : null;
        }
        if (current && typeof current === 'object' && 'data' in current) {
            current = (current as { data: unknown }).data;
            continue;
        }
        return null;
    }

    return null;
};

const notify = (): void => listeners.forEach((listener) => listener());

const isStale = (): boolean => Date.now() >= expiresAt - REFRESH_SKEW_MS;

/**
 * "Is the token still valid?", asked every CHECK_INTERVAL_MS while at least one component is
 * showing assets. Renews it as soon as it is expired or within REFRESH_SKEW_MS of it; does nothing
 * at all while no token has been fetched yet (that first fetch happens on demand, see
 * `signAssetUrl`) or when none is needed.
 */
const startWatch = (): void => {
    if (checkTimer || !isSasRequired) {
        return;
    }
    checkTimer = setInterval(() => {
        const stale = token !== null && isStale();
        const left = token ? `${Math.round((expiresAt - Date.now()) / 60000)} min left` : 'no token yet';
        trace(`validity check (every ${CHECK_INTERVAL_MS / 1000} s): ${left}${stale ? ' -> renewing' : ''}`);
        if (stale) {
            void ensureSasToken();
        }
    }, CHECK_INTERVAL_MS);
};

/** Stopped with the last subscriber - a timer running on in the background only costs requests. */
const stopWatch = (): void => {
    if (checkTimer) {
        clearInterval(checkTimer);
        checkTimer = null;
    }
};

/**
 * Makes sure a valid token is available: hands back the cached one, fetches at most one at a time,
 * and stays quiet for a while after a failure. If the fetch fails (endpoint not deployed yet, no
 * login, 403), behaviour falls back to what it was before - URLs are used without a token, which
 * still works against a public container.
 */
export const ensureSasToken = async (): Promise<string | null> => {
    if (!isSasRequired || !request) {
        return null;
    }
    if (token && !isStale()) {
        return token;
    }
    if (inFlight) {
        return inFlight;
    }
    if (Date.now() < retryAfter) {
        return token;
    }

    // Local copy: `request` could in principle be replaced between the check above and the call
    // below, and a non-null assertion on it would be pure noise.
    const send = request;
    const startedAt = Date.now();

    trace(`${token ? 'renewing' : 'fetching'} SAS token: GET ${sasEndpointUrl}`);

    const pending = (async (): Promise<string | null> => {
        try {
            const next = extractToken(await send(sasEndpointUrl));
            if (!next) {
                throw new Error('response carried no token');
            }
            token = next;
            expiresAt = parseExpiry(next);
            retryAfter = 0;
            const params = new URLSearchParams(next);
            trace(`token after ${Date.now() - startedAt} ms:`, {
                resource: params.get('sr'),
                permissions: params.get('sp'),
                expires: params.get('se'),
                minutesLeft: Math.round((expiresAt - Date.now()) / 60000)
            });
            notify();
            return next;
        } catch (cause) {
            retryAfter = Date.now() + RETRY_DELAY_MS;
            // eslint-disable-next-line no-console
            console.error(
                `Could not fetch the SAS token for the asset container - ${sasEndpointUrl}. ` +
                    'Images/PDFs stay empty for as long as the container is private ' +
                    '(scope: pih.doka.com/catalog.read).',
                cause
            );
            return token;
        } finally {
            inFlight = null;
        }
    })();

    inFlight = pending;
    return pending;
};

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    startWatch();
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            stopWatch();
        }
    };
};

const needsToken = (url: string): boolean => isSasRequired && assetsRoot.length > 0 && url.startsWith(assetsRoot);

/**
 * Appends the SAS token to an asset URL.
 *
 * - `null` when there is nothing to sign (no URL given) - callers show their placeholder instead.
 * - the URL unchanged when it doesn't point into the private container (static offline bundle,
 *   public CDN, placeholder from public/).
 * - `undefined` when a token is needed but none is there yet: "in a moment, but not yet" - the
 *   caller waits instead of loading a URL that is certain to 403 (and would mark the image broken
 *   without ever retrying it).
 */
export const signAssetUrl = (
    url: string | null | undefined,
    sasToken: string | null = token
): string | null | undefined => {
    if (!url) {
        return null;
    }
    if (!needsToken(url)) {
        return url;
    }
    if (!sasToken) {
        // Fetched here - on the first asset that actually needs a token - rather than on mount: the
        // static offline bundle and a public container thus never trigger an API call at all (the
        // offline mode runs without a backend AND without a login). The call is idempotent and
        // allows only one request at a time; once the token arrives, `useAssetUrl`'s subscribers
        // re-render.
        void ensureSasToken();
        return undefined;
    }
    // A token that is about to expire is still used (it is valid right now), but its successor is
    // already on its way - relevant for a page left open for an hour, where nothing else would
    // trigger a renewal.
    if (isStale()) {
        void ensureSasToken();
    }
    return `${url}${url.includes('?') ? '&' : '?'}${sasToken}`;
};

/**
 * The current token as React state - re-renders as soon as a new one arrives, which is what makes
 * the browser reload the `<img src>`/PDF URLs with it after an expiry.
 */
const useSasToken = (): string | null => useSyncExternalStore(subscribe, currentSasToken, currentSasToken);

/**
 * Signs asset URLs with the current SAS token - the only way a blob URL should ever reach the DOM
 * (`<img src>`, PDF viewer). See `signAssetUrl` for the three cases it can return.
 */
export const useAssetUrl = (): ((url: string | null | undefined) => string | null | undefined) => {
    const sasToken = useSasToken();

    return useCallback((url: string | null | undefined) => signAssetUrl(url, sasToken), [sasToken]);
};
